/**
 * Selettore Origine: campo compatto → bottom sheet (iPhone) / popover (iPad, desktop).
 */
import React, { useEffect, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const useIsWide = () => {
  const q = "(min-width: 768px)";
  const [wide, setWide] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setWide(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return wide;
};

function OptionsGrid({ origins, value, onPick }) {
  return (
    <div className="grid grid-cols-2 gap-1.5" data-testid="booking-origin-grid">
      {origins.map((o) => {
        const active = value === o.key;
        return (
          <button key={o.key} type="button" onClick={() => onPick(o.key)}
                  data-testid={`booking-origin-${o.key}`} aria-pressed={active}
                  className={`h-11 px-3 rounded-xl border text-sm text-left flex items-center justify-between gap-1 transition-colors ${
                    active ? "bg-ink-900 text-white border-ink-900"
                           : "bg-white border-sand-300 text-ink-900 hover:border-bronze-400"}`}>
            <span className="truncate">{o.label}</span>
            {active && <Check className="w-4 h-4 flex-shrink-0" />}
          </button>
        );
      })}
    </div>
  );
}

const Trigger = React.forwardRef(({ label, open, ...props }, ref) => (
  <button ref={ref} type="button" {...props} data-testid="booking-origin-toggle" aria-expanded={open}
          className="w-full h-11 flex items-center justify-between rounded-xl bg-white border border-sand-300 px-3">
    <span className="text-[10px] uppercase tracking-[0.18em] text-ink-500">Origine prenotazione</span>
    <span className="flex items-center gap-2 text-sm font-medium text-ink-900" data-testid="booking-origin-current">
      {label}
      <ChevronDown className={`w-4 h-4 text-ink-500 transition-transform ${open ? "rotate-180" : ""}`} />
    </span>
  </button>
));
Trigger.displayName = "OriginTrigger";

export default function ReservationsOriginPicker({ origins, value, onChange }) {
  const [open, setOpen] = useState(false);
  const wide = useIsWide();
  const current = origins.find((o) => o.key === value) || origins[0];
  const pick = (k) => { onChange(k); setOpen(false); };

  if (wide) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild><Trigger label={current?.label} open={open} /></PopoverTrigger>
        <PopoverContent align="end" collisionPadding={12} className="w-[380px] max-h-[60vh] overflow-y-auto rounded-2xl border-sand-200 bg-sand-50 p-3"
                        data-testid="booking-origin-panel">
          <OptionsGrid origins={origins} value={value} onPick={pick} />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <>
      <Trigger label={current?.label} open={open} onClick={() => setOpen(true)} />
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" data-testid="booking-origin-panel"
                      className="rounded-t-3xl bg-sand-50 border-sand-200 px-4 pt-3 max-h-[75dvh] flex flex-col gap-3
                                 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div className="mx-auto w-10 h-1 rounded-full bg-sand-300" />
          <div>
            <SheetTitle className="font-serif text-2xl text-ink-900 font-normal">Origine prenotazione</SheetTitle>
            <SheetDescription className="text-xs text-ink-500">Da dove arriva questa prenotazione?</SheetDescription>
          </div>
          <div className="overflow-y-auto overscroll-contain -mx-1 px-1">
            <OptionsGrid origins={origins} value={value} onPick={pick} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
